import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hasRole, useApi, useApp } from "../context/AppContext";
import { HARDWARE_LABEL, STATUS_STYLE, ago, alertsFor, codeVersionOf, isEmulator, loadFleet } from "../lib/fleet";
import { CabinetsTable } from "./CabinetsTable";
import { RegisterCabinet } from "./cabinet/CabinetAdmin";

// The admin's front page (docs/tasks/35): cabinet types first, then what needs
// a person. A cabinet type is a kind of cabinet on one platform; training rows,
// galleries and (later) the expected model stack belong to it, so every cabinet
// is read against its type.

function StatusBadge({ status }) {
  return <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[status] || STATUS_STYLE.unknown}`}>{status || "unknown"}</span>;
}

function TypeCard({ type, cabinets, training }) {
  const live = cabinets.filter((c) => !c.device?.revoked);
  const online = live.filter((c) => c.health?.online).length;
  const fit = live.filter((c) => c.health?.status === "online").length;
  const counts = { trained: 0, partial: 0, untrained: 0 };
  for (const p of training?.products || []) counts[p.status] = (counts[p.status] || 0) + 1;
  const boards = [...new Set(live.map((c) => HARDWARE_LABEL[c.device?.hardware] || c.device?.hardware).filter(Boolean))];
  return (
    <Link to={`/types/${type.id}`} className="card block space-y-2 hover:border-sky-300 hover:shadow">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold">{type.name}</div>
          <div className="text-xs text-slate-500">{type.model} · {boards.join(", ") || "no cabinet yet"}</div>
        </div>
        <span className="text-xs text-slate-500">{live.length} cabinet{live.length === 1 ? "" : "s"}</span>
      </div>
      <div className="text-xs text-slate-600">
        cameras {(type.cameras || []).map((c) => `${c.id}${c.mount ? ` ${c.mount}` : ""}`).join(", ") || "—"} · shelves {(type.shelves || []).length}
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <span>{online}/{live.length} online</span>
        <span>{fit}/{live.length} fit to sell</span>
        {training && !(training.products || []).length ? <span className="text-slate-500">nothing trained yet</span> : (
          <>
            <span className="text-emerald-700">{counts.trained} trained</span>
            {counts.partial ? <span className="text-amber-700">{counts.partial} partial</span> : null}
            {counts.untrained ? <span className="text-rose-700">{counts.untrained} untrained</span> : null}
          </>
        )}
      </div>
    </Link>
  );
}

// The models that live in the cloud (docs/tasks/35, phase 3): GET models/inventory.
function CloudModels() {
  const api = useApi();
  const { user } = useApp();
  const [inv, setInv] = useState(null);
  const [mode, setMode] = useState(null);
  const [probing, setProbing] = useState(false);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = hasRole(user, "tenant_admin");
  const may = hasRole(user, "tenant_admin", "ml_operator", "device_operator", "auditor");
  useEffect(() => {
    if (!may) return;
    api.getModelsInventory().then(setInv).catch((e) => setErr(String(e.message || e)));
    api.getRecognitionSettings().then(setMode).catch(() => setMode(null));
  }, [api, may]);
  if (!may) return null;
  if (err) return <p className="text-xs text-rose-700">Cloud models: {err}</p>;
  if (!inv) return null;
  const probe = async () => {
    setProbing(true);
    try { setInv(await api.getModelsInventory({ probe: true })); } catch (e) { setErr(String(e.message || e)); } finally { setProbing(false); }
  };
  const enc = inv.encoder || {};
  const svc = inv.classifier?.service || {};
  const classes = svc.probe?.classes;
  return (
    <div className="card">
      <h2 className="mb-2 text-sm font-semibold">Cloud models</h2>
      <dl className="grid grid-cols-[11rem_1fr] gap-y-1 text-sm">
        <dt className="text-slate-500">Gallery encoder</dt>
        <dd className="text-xs">
          {(enc.versions || []).length ? enc.versions.map((v) => `${v.version} (${v.name}, ${String(v.sha256).slice(0, 12)})`).join("; ")
            : `${enc.default?.version} (${enc.default?.name}, ${String(enc.default?.sha256 || "").slice(0, 12)}) - the builder's pin`}
        </dd>
        <dt className="text-slate-500">Detector artefacts</dt>
        <dd className="text-xs">
          {(inv.detector || []).length ? inv.detector.map((d) => d.error ? d.error : `${d.name}: ${d.files.join(", ")}`).join(" · ") : "none in models/detectors/"}
        </dd>
        <dt className="text-slate-500">Cloud check</dt>
        <dd className="text-xs">
          {isAdmin && mode ? (
            <select className="input mr-1 inline-block w-auto py-0.5 text-xs" disabled={saving} value={mode.cloud_check_mode}
              onChange={async (e) => {
                const next = e.target.value;
                const text = { off: "Switch the cloud check off? Disputes from the tablet are then refused.",
                  on_dispute: "Check only when a customer disputes a cart?", auto: "Check every cart that qualifies (look-alikes, weak lines, blind visits) and disputes?" }[next];
                if (!window.confirm(text)) return;
                setSaving(true);
                try { setMode(await api.setRecognitionSettings({ cloud_check_mode: next, devices: mode.devices || {} })); }
                catch (ex) { setErr(String(ex.message || ex)); } finally { setSaving(false); }
              }}>
              {(mode.modes || ["off", "on_dispute", "auto"]).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          ) : <>mode <strong>{mode?.cloud_check_mode || "—"}</strong></>}{mode && Object.keys(mode.devices || {}).length ? ` (${Object.keys(mode.devices).length} cabinet overrides)` : ""} · families {(inv.cloud_check?.families || []).map((f) => f.map((x) => x.slice(0, 8)).join("+")).join("; ") || "none"}</dd>
        <dt className="text-slate-500">Classifier</dt>
        <dd className="text-xs">
          <Link className="mr-1 text-sky-700 hover:underline" to="/classifier">model, classes and retraining →</Link>
          {svc.configured ? `service ${svc.host}` : "no service"}{inv.classifier?.fallback_model_id ? ` · fallback ${inv.classifier.fallback_model_id}` : " · no fallback model"}
          {classes ? <> · knows {classes.length} classes: <span className="font-mono">{classes.map((c) => String(c).slice(0, 8)).join(", ")}</span></> : null}
          {svc.probe && !classes ? <span className="text-rose-700"> · probe: {svc.probe.error || svc.probe.status}</span> : null}
          {svc.configured && !svc.probe && (
            <button type="button" className="ml-2 text-sky-700 hover:underline" disabled={probing} onClick={probe}>
              {probing ? "waking it…" : "read its classes (wakes the container, up to 40 s)"}
            </button>
          )}
        </dd>
        <dt className="text-slate-500">Language model</dt>
        <dd className="text-xs">{inv.vlm?.deployment} · {inv.vlm?.may_act ? "may correct carts" : inv.vlm?.shadow ? "shadow only (recorded, not acted on)" : "off"}</dd>
      </dl>
    </div>
  );
}

function CabinetRow({ cab }) {
  const h = cab.health;
  return (
    <tr>
      <td><Link className="text-sky-700 hover:underline" to={`/cabinets/${cab.id}`}>{cab.device?.name || cab.id}</Link></td>
      <td className="text-xs">{HARDWARE_LABEL[cab.device?.hardware] || cab.device?.hardware || "—"}</td>
      <td><StatusBadge status={h?.status || (cab.device?.revoked ? "revoked" : "unknown")} /></td>
      <td className="text-xs">{ago(h?.last_seen_at || cab.device?.last_seen_at)}</td>
      <td className="font-mono text-xs">{codeVersionOf(cab) || "—"}</td>
      <td className="font-mono text-xs">{h?.model?.reported || h?.report?.model_version || "—"}</td>
    </tr>
  );
}

export function FleetPage() {
  const api = useApi();
  const { user } = useApp();
  const [fleet, setFleet] = useState(null);
  const [training, setTraining] = useState({});
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadFleet(api, (partial) => !cancelled && setFleet((cur) => cur || partial)).then((f) => {
      if (cancelled) return;
      setFleet(f);
      Promise.all(f.types.map((t) => api.getCabinetTypeTrainingStatus(t.id).then((s) => [t.id, s]).catch(() => [t.id, null])))
        .then((pairs) => !cancelled && setTraining(Object.fromEntries(pairs)));
    }).catch((e) => !cancelled && setErr(String(e.message || e)));
    return () => { cancelled = true; };
  }, [api]);

  if (err) return <p className="text-sm text-rose-700">{err}</p>;
  if (!fleet) return <p className="text-sm text-slate-500">Loading the fleet…</p>;

  const byType = {};
  for (const c of fleet.cabinets) if (c.type) (byType[c.type.id] = byType[c.type.id] || []).push(c);
  // Until every cabinet has answered, its type and health are unknown, not missing.
  const loading = fleet.cabinets.some((c) => c.loading);
  const unassigned = loading ? [] : fleet.cabinets.filter((c) => !c.type && !c.device?.revoked && !isEmulator(c));
  const emulators = fleet.cabinets.filter((c) => isEmulator(c) && !c.device?.revoked);
  const alerts = (loading ? [] : fleet.cabinets)
    .map((c) => ({ cab: c, items: alertsFor(c, fleet.newestRuntime) }))
    .filter((a) => a.items.length);
  const mayEditTypes = hasRole(user, "tenant_admin", "device_operator", "ml_operator");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Fleet</h1>
          <p className="text-sm text-slate-500">
            Cabinet types, the cabinets of each, and what needs attention. A type is a kind of cabinet on one platform:
            its training rows and galleries are shared by every cabinet of the type.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasRole(user, "tenant_admin", "device_operator") && <RegisterCabinet onDone={() => loadFleet(api).then(setFleet).catch(() => {})} />}
          {mayEditTypes && <Link to="/types/new" className="btn btn-secondary">New cabinet type</Link>}
        </div>
      </div>

      {fleet.errors.devices && (
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Your role cannot list cabinets ({fleet.errors.devices.message || "forbidden"}); only the cabinet types are shown.
        </p>
      )}

      {loading && <p className="text-xs text-slate-500">Asking each cabinet for its condition… (after a quiet spell the cloud takes a few seconds to wake)</p>}

      {alerts.length > 0 && (
        <div className="card">
          <h2 className="mb-2 text-sm font-semibold">Needs attention</h2>
          <ul className="space-y-1 text-sm">
            {alerts.map(({ cab, items }) => (
              <li key={cab.id}>
                <Link className="font-medium text-sky-700 hover:underline" to={`/cabinets/${cab.id}`}>{cab.device?.name || cab.id}</Link>
                {": "}
                {items.map((a, i) => (
                  <span key={i} className={a.level === "bad" ? "text-rose-700" : a.level === "warn" ? "text-amber-700" : "text-slate-600"}>
                    {i ? "; " : ""}{a.text}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase text-slate-500">Cabinet types</h2>
        {fleet.types.length === 0 ? <p className="text-sm text-slate-500">No cabinet types yet.</p> : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {fleet.types.map((t) => <TypeCard key={t.id} type={t} cabinets={byType[t.id] || []} training={training[t.id]} />)}
          </div>
        )}
      </div>

      {fleet.cabinets.length > 0 && <CabinetsTable cabinets={fleet.cabinets} newestRuntime={fleet.newestRuntime} />}

      {unassigned.length > 0 && (
        <div className="card overflow-x-auto">
          <h2 className="mb-1 text-sm font-semibold">Cabinets without a type</h2>
          <p className="mb-2 text-xs text-slate-500">
            A cabinet needs a type to be trained and to get a gallery of its own. Open one to assign it.
          </p>
          <table className="table">
            <thead><tr><th>Cabinet</th><th>Board</th><th>Status</th><th>Last seen</th><th>Code</th><th>Gallery</th></tr></thead>
            <tbody>{unassigned.map((c) => <CabinetRow key={c.id} cab={c} />)}</tbody>
          </table>
        </div>
      )}

      <CloudModels />

      {emulators.length > 0 && (
        <p className="text-xs text-slate-500">
          Cloud emulators (no hardware):{" "}
          {emulators.map((c, i) => <span key={c.id}>{i ? ", " : ""}<Link className="text-sky-700 hover:underline" to={`/cabinets/${c.id}`}>{c.device?.name || c.id}</Link></span>)}
        </p>
      )}

      {fleet.newestRuntime && (
        <p className="text-xs text-slate-500">Newest runtime release: <span className="font-mono">{fleet.newestRuntime}</span></p>
      )}
    </div>
  );
}

export { CabinetRow, StatusBadge };
